import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { ContentType } from "@/types/ab-tests";
import { getTestableFields } from "@/utils/ab-test-utils";

interface EditFieldsDialogProps {
  open: boolean;
  selectedFields: ContentType[];
  bulletPoints: string[];
  onSelectedFieldsChange: (fields: ContentType[]) => void;
  onCancel: () => void;
  onSave: () => void;
}

export function EditFieldsDialog({
  open,
  selectedFields,
  bulletPoints,
  onSelectedFieldsChange,
  onCancel,
  onSave,
}: EditFieldsDialogProps) {
  const toggle = (field: ContentType) => {
    onSelectedFieldsChange(
      selectedFields.includes(field)
        ? selectedFields.filter(f => f !== field)
        : [...selectedFields, field],
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onCancel(); }}>
      <DialogContent className="sm:max-w-lg bg-card">
        <DialogHeader>
          <DialogTitle>Edit testing fields</DialogTitle>
          <DialogDescription>
            We recommend selecting just one field per test — isolating a single change produces the most valuable results.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2.5 py-2">
          {getTestableFields(bulletPoints.length).map((field) => (
            <div key={field.value} className="flex items-center gap-2">
              <Checkbox
                id={`edit-field-${field.value}`}
                checked={selectedFields.includes(field.value)}
                onCheckedChange={() => toggle(field.value)}
              />
              <label htmlFor={`edit-field-${field.value}`} className="text-sm cursor-pointer select-none">
                {field.label}
              </label>
            </div>
          ))}
          {selectedFields.length > 1 && (
            <div className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400 pt-1">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Testing multiple fields at once reduces result specificity — it becomes harder to isolate which change drove the outcome.</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onSave} disabled={selectedFields.length === 0}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
